// useRequestFilter – URL-backed request filter: active request, device ids, latest commands, activate/clear.

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { CommandDdbItem } from '../../../../types/Commands';
import { parseRequestRow, type RequestRowLike } from './deviceListHelpers';
import { formatApiResponseError } from '../../EventsManagementView/utils';
import {
  commandTargetRearDeviceId,
  isFrontDeviceId,
  normalizeFleetStatusDeviceId,
  pairedFrontIdFromRear,
} from '../../../helpers/devicePairing';

import type { UseRequestFilterResult } from '../types/DeviceListViewTypes';

export type { UseRequestFilterResult };

const REQUEST_RECONCILE_MS = 60_000;
const TERMINAL_COMMAND_STATUSES = new Set([
  'SUCCEEDED',
  'SUCCESS',
  'COMPLETE',
  'COMPLETED',
  'FAILED',
  'TIMED_OUT',
  'TIMEDOUT',
  'CANCELLED',
  'CANCELED',
]);

type UseRequestFilterOptions = {
  operatorScopedEventId?: string | null;
};

function normalizeCommandStatus(status: unknown): string {
  return String(status ?? '').trim().toUpperCase();
}

function isTerminalCommandStatus(status: unknown): boolean {
  return TERMINAL_COMMAND_STATUSES.has(normalizeCommandStatus(status));
}

function areRequestRowsTerminal(rows: CommandDdbItem[]): boolean {
  return rows.length > 0 && rows.every((row) => isTerminalCommandStatus(row.status));
}

function deriveCommandTargetDeviceIds(cmd: Partial<CommandDdbItem>): string[] {
  const deviceId = typeof cmd.deviceId === 'string' ? cmd.deviceId.trim() : '';
  if (!deviceId) return [];
  const normalizedDeviceId = normalizeFleetStatusDeviceId(deviceId);

  const type = String(cmd.type ?? '').trim().toUpperCase();
  let isFrontTarget = type.startsWith('FRONT_');

  const rawStatusInfo = typeof cmd.status_info === 'string' ? cmd.status_info.trim() : '';
  if (!isFrontTarget && rawStatusInfo) {
    try {
      const parsed = JSON.parse(rawStatusInfo) as { ack?: { target?: unknown } };
      const ackTarget = String(parsed?.ack?.target ?? '').trim().toLowerCase();
      if (ackTarget === 'front') isFrontTarget = true;
    } catch {
      // Ignore malformed status payloads.
    }
  }

  if (isFrontTarget) {
    const frontId = isFrontDeviceId(normalizedDeviceId)
      ? normalizedDeviceId
      : pairedFrontIdFromRear(normalizedDeviceId);
    return frontId ? [frontId] : [normalizedDeviceId];
  }
  return [commandTargetRearDeviceId(normalizedDeviceId)];
}

function buildLatestByDevice(rows: CommandDdbItem[]): Record<string, CommandDdbItem> {
  const byDevice: Record<string, CommandDdbItem> = {};
  for (const ddb of rows) {
    const targetDeviceIds = deriveCommandTargetDeviceIds(ddb);
    if (!targetDeviceIds.length) continue;
    const updated = typeof ddb.updatedAt === 'number' ? ddb.updatedAt : 0;
    for (const targetId of targetDeviceIds) {
      const existing = byDevice[targetId];
      const existingUpdated = existing && typeof existing.updatedAt === 'number' ? existing.updatedAt : 0;
      if (!existing || updated >= existingUpdated) {
        byDevice[targetId] = { ...ddb, deviceId: targetId };
      }
    }
  }
  return byDevice;
}

export function useRequestFilter(
  options: UseRequestFilterOptions = {},
): UseRequestFilterResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const scopedEventId = String(options.operatorScopedEventId ?? '').trim();

  const [activeRequestId, setActiveRequestId] = useState<string | null>(null);
  const [requestFilterIds, setRequestFilterIds] = useState<string[] | null>(null);
  const [requestLatestCommandMap, setRequestLatestCommandMap] = useState<
    Record<string, CommandDdbItem>
  >({});
  const [requestCommandsTerminal, setRequestCommandsTerminal] = useState(false);

  const requestSeqRef = useRef(0);
  const activeRequestIdRef = useRef<string | null>(null);

  useEffect(() => {
    activeRequestIdRef.current = activeRequestId;
  }, [activeRequestId]);

  function setUrlRequestId(next: string | null) {
    const p = new URLSearchParams(searchParams);
    if (!next) p.delete('requestId');
    else p.set('requestId', next);
    setSearchParams(p);
  }

  function clearActiveRequest() {
    requestSeqRef.current += 1;
    setActiveRequestId(null);
    setRequestFilterIds(null);
    setRequestLatestCommandMap({});
    setRequestCommandsTerminal(false);
    setUrlRequestId(null);
    navigate('/devices', { replace: true });
  }

  function clearActiveRequestStateOnly() {
    requestSeqRef.current += 1;
    setActiveRequestId(null);
    setRequestFilterIds(null);
    setRequestLatestCommandMap({});
    setRequestCommandsTerminal(false);
  }

  async function activateRequest(requestId: string, seedDeviceIds?: string[]) {
    const rid = String(requestId || '').trim();
    if (!rid) return;

    const seq = requestSeqRef.current + 1;
    requestSeqRef.current = seq;

    setActiveRequestId(rid);
    setRequestFilterIds(seedDeviceIds?.length ? seedDeviceIds : null);
    setRequestLatestCommandMap({});
    setRequestCommandsTerminal(false);

    try {
      const res = scopedEventId
        ? await window.eventsAPI.listEventRequests(scopedEventId)
        : await window.eventsAPI.listRequests();
      if (requestSeqRef.current !== seq) return;

      if (!res?.ok) {
        throw new Error(formatApiResponseError(res, 'Failed to load requests'));
      }

      const data = res?.data as { rows?: unknown } | undefined;
      const rows2: RequestRowLike[] = Array.isArray(data?.rows)
        ? (data.rows as RequestRowLike[])
        : [];
      const found = rows2.map(parseRequestRow).find((r) => r?.requestId === rid);

      if (found?.deviceIds?.length) setRequestFilterIds(found.deviceIds);
      else if (!seedDeviceIds?.length) setRequestFilterIds([]);
    } catch {
      if (requestSeqRef.current !== seq) return;
      if (!seedDeviceIds?.length) setRequestFilterIds([]);
    }

    try {
      const res = scopedEventId
        ? await window.eventsAPI.getEventRequestCommands(scopedEventId, rid)
        : await window.eventsAPI.getRequestCommands(rid);
      if (requestSeqRef.current !== seq) return;

      if (!res?.ok) {
        throw new Error(formatApiResponseError(res, 'Failed to load request commands'));
      }

      const cmdData = res?.data as { rows?: unknown } | undefined;
      const base = Array.isArray(cmdData?.rows) ? (cmdData.rows as CommandDdbItem[]) : [];
      setRequestLatestCommandMap(buildLatestByDevice(base));
      setRequestCommandsTerminal(areRequestRowsTerminal(base));
    } catch {
      if (requestSeqRef.current !== seq) return;
      setRequestLatestCommandMap({});
      setRequestCommandsTerminal(false);
    }
  }

  /** When URL has requestId, activate that request; when it's removed, clear filter. */
  useEffect(() => {
    const rid = String(searchParams.get('requestId') || '').trim();

    if (!rid) {
      if (activeRequestIdRef.current) clearActiveRequestStateOnly();
      return;
    }

    if (rid === activeRequestIdRef.current) return;
    void activateRequest(rid);
  }, [searchParams, scopedEventId]);

  useEffect(() => {
    const rid = String(activeRequestId ?? '').trim();
    if (!rid || requestCommandsTerminal) return;

    let cancelled = false;

    const refreshOnce = async () => {
      try {
        const res = scopedEventId
          ? await window.eventsAPI.getEventRequestCommands(scopedEventId, rid)
          : await window.eventsAPI.getRequestCommands(rid);
        if (cancelled || !res?.ok) return;
        const cmdData = res?.data as { rows?: unknown } | undefined;
        const base = Array.isArray(cmdData?.rows) ? (cmdData.rows as CommandDdbItem[]) : [];
        setRequestLatestCommandMap((prev) => ({ ...prev, ...buildLatestByDevice(base) }));
        if (areRequestRowsTerminal(base)) setRequestCommandsTerminal(true);
      } catch {
        // Keep previous map on transient reconciliation errors.
      }
    };

    const timer = setInterval(() => {
      void refreshOnce();
    }, REQUEST_RECONCILE_MS);

    function onFocus(): void {
      void refreshOnce();
    }

    window.addEventListener('focus', onFocus);
    const unsubscribeReconnect = window.fleetAPI?.onWsReconnected?.(() => {
      void refreshOnce();
    });

    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      unsubscribeReconnect?.();
    };
  }, [activeRequestId, scopedEventId, requestCommandsTerminal]);

  useEffect(() => {
    const rid = String(activeRequestId ?? '').trim();
    if (!rid || requestCommandsTerminal || !requestFilterIds?.length) return;

    const allKnownTargetsTerminal = requestFilterIds.every((deviceId) => {
      const id = String(deviceId ?? '').trim();
      return id.length > 0 && isTerminalCommandStatus(requestLatestCommandMap[id]?.status);
    });

    if (allKnownTargetsTerminal) setRequestCommandsTerminal(true);
  }, [activeRequestId, requestCommandsTerminal, requestFilterIds, requestLatestCommandMap]);

  return {
    activeRequestId,
    requestFilterIds,
    requestLatestCommandMap,
    setRequestLatestCommandMap,
    activeRequestIdRef,
    setUrlRequestId,
    clearActiveRequest,
    clearActiveRequestStateOnly,
    activateRequest,
  };
}
